// src/pages/PLCDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend
);

function formatDate(d) {
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

function prettyDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function PLCDetail() {
  const { plcId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [plcName, setPlcName] = useState("");
  const [latest, setLatest] = useState({});
  const [histories, setHistories] = useState({});
  const [selectedVars, setSelectedVars] = useState([]);
  const [importantVars, setImportantVars] = useState([]);
  const [hasData, setHasData] = useState({});

  // Per-variable selected date
  const [dates, setDates] = useState({});

  const today = new Date();

  useEffect(() => {
    if (!plcId) return;

    const fetchInitial = async () => {
      try {
        // Important vars
        const vRes = await fetch(`/api/plc/${plcId}/important_vars`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        let important = [];
        if (vRes.ok) {
          const json = await vRes.json();
          important = json.important_vars || [];
          setImportantVars(important);
        }

        // Latest
        const res = await fetch(`/api/plc/${plcId}/latest`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          if (res.status === 401) {
            localStorage.removeItem("token");
            window.location.href = "/login";
          }
          return;
        }

        const json = await res.json();
        setLatest(json);
        setPlcName(`PLC ${plcId}`);

        const vars =
          important.length > 0 ? important : Object.keys(json || {});
        const initialVars = vars.slice(0, 4);
        setSelectedVars(initialVars);

        const initialDates = {};
        initialVars.forEach((v) => (initialDates[v] = new Date(today)));
        setDates(initialDates);

        initialVars.forEach((v) =>
          fetchHistory(v, initialDates[v])
        );
      } catch (err) {
        console.error(err);
      }
    };

    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plcId]);

  const fetchHistory = async (variable, dateObj) => {
    try {
      const res = await fetch(
        `/api/plc/${plcId}/history/${encodeURIComponent(variable)}?date=${formatDate(dateObj)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      if (res.ok) {
        const data = await res.json();
  
        setHistories((prev) => ({
          ...prev,
          [variable]: data.map((r) => ({
            x: new Date(r.timestamp),
            y: r.value,
          })),
        }));
  
        setHasData((prev) => ({
          ...prev,
          [variable]: data.length > 0,
        }));
      }
    } catch (err) {
      console.error("History fetch error", variable, err);
    }
  };  

  const toggleVar = (v) => {
    setSelectedVars((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );

    if (!dates[v]) {
      const d = new Date(today);
      setDates((prev) => ({ ...prev, [v]: d }));
      fetchHistory(v, d);
    }
  };

  const shiftDay = (v, delta) => {
    const newDate = new Date(dates[v]);
    newDate.setDate(newDate.getDate() + delta);

    setDates((prev) => ({ ...prev, [v]: newDate }));
    fetchHistory(v, newDate);
  };

  const chartOptions = {
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const d = items[0].parsed.x;
            return new Date(d).toLocaleString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              day: "numeric",
              month: "short",
            });
          },
          label: (ctx) => `${ctx.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "hour" },
        title: { display: true, text: "Time" },
      },
      y: {
        title: { display: true, text: "Value" },
      },
    },
  };

  const displayVars =
    importantVars.length > 0
      ? importantVars
      : Object.keys(latest || {});

  return (
    <div>
      <Navbar />
      <Box sx={{ p: 3, pt: 12, minHeight: "70vh" }}>
        <Button variant="outlined" onClick={() => navigate("/plc")}>
          ← Back to PLCs
        </Button>

        <Typography variant="h4" sx={{ mt: 2 }}>
          {plcName}
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2">Variables</Typography>
          <Box sx={{ mt: 1 }}>
            {displayVars.map((v) => (
              <Chip
                key={v}
                label={v}
                onClick={() => toggleVar(v)}
                color={selectedVars.includes(v) ? "primary" : "default"}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {selectedVars.map((v) => (
            <Grid item xs={12} md={6} key={v}>
              <Paper sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">{v}</Typography>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => shiftDay(v, -1)}
                      disabled={hasData[v] === false}
                    >
                      <ArrowBackIosNewIcon fontSize="inherit" />
                    </IconButton>

                    <Typography sx={{ mx: 1 }}>
                      {prettyDate(dates[v])}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => shiftDay(v, 1)}
                      disabled={formatDate(dates[v]) === formatDate(today)}
                    >
                      <ArrowForwardIosIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </Box>

                <Line
                  options={chartOptions}
                  data={{
                    datasets: [
                      {
                        data: histories[v] || [],
                        tension: 0.1,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                      },
                    ],
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Footer />
    </div>
  );
}

export default PLCDetail;
